import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { AngularFirestore } from '@angular/fire/compat/firestore';
import { Observable, of } from 'rxjs';
import { map, catchError, take } from 'rxjs/operators';
import { ApiResponse } from 'src/app/models/api-response.model';
import { ClientDeactivationImpact, ClientDeactivationRun, ClientReactivationResult } from 'src/app/models/client-deactivation.model';
import { Client, ClientContact, ClientDefaults, MedicareGEO } from 'src/app/models/client.model';
import { normalizeStediNpi } from 'src/app/models/client.util';
import { Patient } from 'src/app/models/patient.model';
import { Departament } from 'src/app/shared/components/client-detail/client-department-list/client-department.model';
import { environment } from 'src/environments/environment';
import { client_rates_default, client_vendors_default } from './../../models/clients/client-default-values';
import { SnackService } from './../snack.service';
import {
  CreatePhoneConfig,
  CreatePhoneConfigEntry,
  PhoneConfig,
  PhoneConfigEntry,
  UpdatePhoneConfig,
  UpdatePhoneConfigEntry,
} from 'src/app/shared/models/phone-config.model';

@Injectable({
  providedIn: 'root',
})
export class ClientServicesService {
  clientDefaults: ClientDefaults;
  welbyCoreApiBaseUrl = environment.welbyEndpoint;
  baseUrl = `${environment.welbyEndpoint}/api/v1/core/clients`;
  departmentsUrl = `${environment.welbyEndpoint}/api/v1/clients/departments`;
  constructor(
    private db: AngularFirestore,
    private snack: SnackService,
    private http: HttpClient
  ) {
    this.setClientDefaults();
  }
  /**
   * Saves a new client to the FB
   *
   * @param client - client object
   */
  async saveNewClient(client: Client): Promise<void> {
    const initialContact: ClientContact = {
      primary_email: client.admin_email,
      primary_phone: '',
      roles: {
        admin: true,
        billing: false,
        clinical: false,
      },
      uid: 'tbd',
      active: true,
    };
    let client_id;
    let client_ids;
    await this.db
      .collection('clients')
      .add(client)
      .then(async (docRef) => {
        client_id = docRef.id;
        client_ids = { uid: client_id, npi: client.client_npi };
        await docRef.set({ client_id, client_ids }, { merge: true });
        await docRef.collection('client_contacts').add(initialContact);
      })
      .catch((error) => {
        this.snack.patientLoaded(error);
      });
    this.snack.genericSnackBar(`${client.client_name} / ${client_id} has been created.`, ['success-snackbar'], 4000);
  }
  /**
   * Builds a client FS object from the client form
   *
   * @param client - client object
   */
  buildClient(client): Client {
    const rpm = { active: true, rate: Number(client.rpm_clinical_rate) };
    const ccm = { active: true, rate: Number(client.ccm_clinical_rate) };
    const license = { active: true, rate: Number(client.software_license_rate) };

    const emr = {
      emr_access: false,
      emr_login_id: 'N/A',
      emr_name: client.emr.vendor,
      credentials: {
        access_token: 'N/A',
        auth_endpoint: client?.emr.auth_url ? client.emr.auth_url : 'N/A',
        data_endpoint: client?.emr.base_url ? client.emr.base_url : 'N/A',
        expires_in: '3600',
        scope: client?.emr?.scopes_needed ? client.emr.scopes_needed : 'N/A',
        token_endpoint: client?.emr?.token_url ? client.emr.token_url : 'N/A',
        token_type: 'Bearer',
      },
      metadata: {
        ...(client?.emr?.metadata ?? {}),
        practice_id: client?.welby_practice_id || null,
        department_id: client?.department_id || null,
        supervising_provider_id: client?.supervising_provider_id || null,
      },
      welby_practice_id: client?.welby_practice_id || null,
    };

    const clientRecord: Client = {
      active: true,
      admin_email: client.admin_email,
      billing_approved: false,
      client_id: '',
      client_name: client.client_name,
      client_npi: client.client_npi,
      client_stedi_npi: normalizeStediNpi(client.client_stedi_npi),
      emr_data: emr,
      rates: client_rates_default,
      patients_loaded: 0,
      device_vendors: client_vendors_default,
      services: {
        ...this.clientDefaults.client_services_default,
        software_license: license,
        ccm_clinical: ccm,
        rpm_clinical: rpm,
      },
      start_date: new Date(),
    };
    return clientRecord;
  }
  /**
   * Get a client by id
   *
   * @param client_id - client id
   */
  getClientById(client_id: string) {
    return this.db.collection('clients').doc(client_id).get().toPromise();
  }

  /**
   * Update a client
   *
   * @param client_id - client id
   * @param body client object
   */
  async updateClient(client_id: string, body: Client): Promise<any> {
    try {
      await this.db.collection('clients').doc(client_id).update(body);
      this.snack.genericSnackBar(`${body.client_name} has been updated.`, ['success-snackbar'], 4000);
    } catch (error) {
      this.snack.genericSnackBar(`An error while saving client.`, ['error-snackbar'], 4000);
    }
  }

  updateClientService(client_id: string, body: Client): Observable<any> {
    const url = `${this.baseUrl}/${client_id}`;
    return this.http.put(url, body);
  }
  /**
   * A user's clinic-membership rows, with their row ids.
   */
  async getClientAccessRows(userId: string): Promise<{ id: string; client_account_id: string }[]> {
    const snap = await this.db
      .collection('client_account_to_user', (ref) => ref.where('user_id', '==', userId))
      .get()
      .pipe(take(1))
      .toPromise();
    return snap.docs.map((row) => ({ id: row.id, client_account_id: (row.data() as any)?.client_account_id }));
  }

  /**
   * Add a client account to a user
   *
   * @param client_id - client id
   * @param user_id - user id
   */
  async addClientAccount2User(clientId: string, userId: string): Promise<void> {
    const clientAccount2UserCollection = this.db.collection('client_account_to_user');
    await clientAccount2UserCollection.add({ client_account_id: clientId, user_id: userId });
  }

  /**
   * Deletes membership rows by row id, in one batch.
   */
  async deleteClientAccessRows(rowIds: string[]): Promise<void> {
    const batch = this.db.firestore.batch();
    rowIds.forEach((rowId) => batch.delete(this.db.firestore.collection('client_account_to_user').doc(rowId)));
    await batch.commit();
  }

  getPatientsByClient(clientId: string): Observable<Patient[]> {
    return this.db.collection('users', (ref) => ref.where('client_responsible_id', '==', clientId)).valueChanges();
  }

  /**
   * How many active patients terminating a client would unenrol — the number the confirmation is
   * built around, so it must not be an estimate.
   *
   * Answered by the backend rather than counted here. This used to run the query in the browser, and
   * the compat web SDK has no `select()`: on the 3000-chart panels this feature exists for, that
   * pulled 3000 complete patient documents — names, dates of birth, MRNs — over the wire and billed
   * 3000 reads, to render one number in a dialog. `ClientDeactivationService.countActivePatients`
   * already does it with a projected, paged query and returns only the figure.
   */
  getClientDeactivationImpact(clientId: string): Observable<ClientDeactivationImpact> {
    return this.http
      .get<ApiResponse<ClientDeactivationImpact>>(`${this.clientDeactivationUrl(clientId)}/impact`)
      .pipe(map((response) => response.data));
  }

  setClientDefaults(): void {
    this.clientDefaults = {} as ClientDefaults;
    this.getClientDefaults().then((clientDefaults) => {
      clientDefaults.docs.forEach((doc) => (this.clientDefaults[doc.id] = doc.data()));
    });
  }

  async getClientDefaults(): Promise<any> {
    return this.db.collection('client_defaults').get().toPromise();
  }

  filterMedicareGEO(state: string): Observable<MedicareGEO[]> {
    return this.db
      .collection('medicare_geo_rates', (ref) => ref.where('state', '>=', state).where('state', '<=', state + '\uf8ff'))
      .snapshotChanges()
      .pipe(
        map((actions) =>
          actions.map((a) => {
            const data = a.payload.doc.data() as Record<string, any>;
            const id = a.payload.doc.id;
            return { ...data, id };
          })
        )
      );
  }

  getClientsById(clientId: string): Observable<Client> {
    return this.db.collection('clients').doc(clientId).valueChanges();
  }

  getClientDepartments(clientId: string): Observable<Departament[]> {
    const url = `${this.departmentsUrl}?clientId=${clientId}`;
    return this.http.get<Departament[]>(url);
  }

  saveClientDepartment(data: Departament): Observable<Departament> {
    return this.http.post<Departament>(this.departmentsUrl, data);
  }

  updateClientDepartment(docId: string, data: Partial<Departament>): Observable<Departament> {
    const url = `${this.departmentsUrl}/${docId}`;
    return this.http.put<Departament>(url, data);
  }

  deleteClientDepartment(docId: string): Observable<void> {
    const url = `${this.departmentsUrl}/${docId}`;
    return this.http.delete<void>(url);
  }

  // Phone Configuration Methods
  getClientPhoneConfig(clientId: string): Observable<PhoneConfig | null> {
    const url = `${this.welbyCoreApiBaseUrl}/api/v1/clients/${clientId}/phone`;
    return this.http.get<PhoneConfig>(url).pipe(
      map(response => {
        if (!response) {
          return null;
        }
        return {
          provider: response.provider,
          phoneNumber: response.phoneNumber,
          // Must stay `isActive`: this method's contract is `PhoneConfig`, which declares
          // `isActive`. Emitting `active` here left every consumer reading `isActive` as
          // undefined, so a deactivated line rendered as Active in the admin dialog.
          isActive: response.isActive,
          createdAt: response.createdAt ? new Date(response.createdAt) : new Date(),
          createdBy: response.createdBy
        };
      })
    );
  }

  saveClientPhoneConfig(clientId: string, config: CreatePhoneConfig): Observable<PhoneConfig> {
    const url = `${this.welbyCoreApiBaseUrl}/api/v1/clients/${clientId}/phone`;
    return this.http.post<PhoneConfig>(url, {
      provider: config.provider,
      phoneNumber: config.phoneNumber,
      active: config.isActive
    });
  }

  updateClientPhoneConfig(clientId: string, config: UpdatePhoneConfig): Observable<PhoneConfig> {
    const url = `${this.welbyCoreApiBaseUrl}/api/v1/clients/${clientId}/phone`;
    return this.http.put<PhoneConfig>(url, {
      provider: config.provider,
      phoneNumber: config.phoneNumber,
      active: config.isActive
    });
  }

  // Multi-line phone configuration (NP-04). These target the plural `/phones` routes, which own
  // `clients/{clientId}.phoneConfigs`; the singular `/phone` methods above still drive the legacy
  // mirror object and are kept for callers that have not moved over.
  //
  // Unlike the singular routes, these are new controllers and therefore return the standardized
  // `ApiResponse` envelope, so `.data` is unwrapped here and never in a component. The two shapes
  // coexist in this service on purpose — do not unify them.
  getClientPhoneConfigs(clientId: string): Observable<PhoneConfigEntry[]> {
    return this.http
      .get<ApiResponse<PhoneConfigEntry[]>>(this.clientPhonesUrl(clientId))
      .pipe(map((response) => response?.data ?? []));
  }

  /** The backend derives `provider`, `region` and `isoCountry` from the number, so none are sent. */
  addClientPhoneConfig(clientId: string, entry: CreatePhoneConfigEntry): Observable<PhoneConfigEntry> {
    return this.http
      .post<ApiResponse<PhoneConfigEntry>>(this.clientPhonesUrl(clientId), {
        phoneNumber: entry.phoneNumber,
        isActive: entry.isActive,
      })
      .pipe(map((response) => response.data));
  }

  /**
   * Keyed by the entry's backend id, never by the phone number: an E.164 number url-encodes to
   * `%2B1...`, and proxies that decode `%2B` to a space turn that into an intermittent failure.
   */
  patchClientPhoneConfig(clientId: string, entryId: string, changes: UpdatePhoneConfigEntry): Observable<PhoneConfigEntry> {
    const url = `${this.clientPhonesUrl(clientId)}/${encodeURIComponent(entryId)}`;
    return this.http.patch<ApiResponse<PhoneConfigEntry>>(url, changes).pipe(map((response) => response.data));
  }

  // Client deactivation (terminating a clinic). Like the plural `/phones` routes above, these are
  // new controllers returning the standardized `ApiResponse` envelope, so `.data` is unwrapped here
  // and never in a component.

  /**
   * Starts unenrolling the client's panel and deactivating the client.
   *
   * Returns as soon as the run is accepted — the panel is processed in the background, so the
   * returned run is always `running` with a zero count. Progress comes from polling
   * `getClientDeactivationRun`.
   */
  startClientDeactivation(clientId: string): Observable<ClientDeactivationRun> {
    return this.http
      .post<ApiResponse<ClientDeactivationRun>>(this.clientDeactivationUrl(clientId), {})
      .pipe(map((response) => response.data));
  }

  /** The newest run for a client, or null when it has never been deactivated. */
  getClientDeactivationRun(clientId: string): Observable<ClientDeactivationRun | null> {
    return this.http
      .get<ApiResponse<ClientDeactivationRun>>(this.clientDeactivationUrl(clientId))
      .pipe(map((response) => response?.data ?? null));
  }

  /**
   * Makes a deactivated client active again. Its patients are NOT re-enrolled.
   *
   * Replaces the generic `PUT /core/clients/:id` with `{ active: true }`, which any logged-in user
   * could call and which recorded nothing: this route re-checks `isAdmin` and stamps who did it.
   */
  reactivateClient(clientId: string): Observable<ClientReactivationResult> {
    return this.http
      .post<ApiResponse<ClientReactivationResult>>(`${this.welbyCoreApiBaseUrl}/api/v1/clients/${clientId}/reactivation`, {})
      .pipe(map((response) => response.data));
  }

  private clientPhonesUrl(clientId: string): string {
    return `${this.welbyCoreApiBaseUrl}/api/v1/clients/${clientId}/phones`;
  }

  private clientDeactivationUrl(clientId: string): string {
    return `${this.welbyCoreApiBaseUrl}/api/v1/clients/${clientId}/deactivation`;
  }
}
